import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Customer } from '../model/customer.model';
import { Observable } from 'rxjs/index';
import { ApiResponse } from '../model/api.response';
import { AuditLog } from '../model/audit-log.model';
import { Employee } from '../model/employee.model';
import { Bill } from '../model/bill.model';
import { NewExpense } from 'src/app/model/newexpense.model';
import { DashboardData } from '../model/dashboard.model';
import { AreaData } from '../model/area.model';
import { environment } from './../../environments/environment';
import { MeterData } from '../model/meter.model';
import { Complaint } from '../model/complaint';
import { Payment } from '../dialogs/billdetails/billdetails.component';
import { BankAmount, BankAmountRequest, BankAmountResponse, BankAmountUpdateResponse } from '../model/bank.model';
import { PhoneMatchResult, BillUpdateRequest } from '../model/phone-match.model';

@Injectable()
export class ApiService {
  constructor(private http: HttpClient) {}
  baseUrl: string = environment.apiServiceBaseUrl;
  
  login(loginPayload): Observable<ApiResponse> {
    return this.http.get<ApiResponse>(
      this.baseUrl + '/hawdco/employees/admin-login',
      { params: loginPayload }
    );
  }

  getAuditLogs(): Observable<AuditLog[]> {
    return this.http.get<AuditLog[]>(
      this.baseUrl + '/hawdco/audit-logs'
    );
  }

  getMeterChanges(): Observable<AuditLog[]> {
    return this.http.get<AuditLog[]>(
      this.baseUrl + '/hawdco/audit-logs/meter-changes'
    );
  }

  getCustomers(
    pageNumber: any=0,
    pageSize: any=15,
    filterOption: any=0,
    filterValue: any=""
  ): Observable<ApiResponse> {
    const loggedInUser = window.localStorage.getItem('loggedInUser');
    const payload = {
      employeeId: loggedInUser,
      page: pageNumber,
      size: pageSize,
      filterId: filterOption,
      filterValue: filterValue
    };
    return this.http.get<ApiResponse>(
      this.baseUrl + '/hawdco/customers/employees/customers',
      { params: payload }
    );
  }

  getCustomersByEmpId(): Observable<ApiResponse> {
    return this.http.get<ApiResponse>(
      this.baseUrl + '/hawdco/customers/employees/customers'
    );
  }

  getAllCustomers(): Observable<ApiResponse> {
    return this.http.get<ApiResponse>(
      `${this.baseUrl}/hawdco/customers`,
      { params: { size: '100000' } }
    );
  }

  getCurrentCycleBills(customerId: number): Observable<any> {
    return this.http.get<any>(
      `${this.baseUrl}/hawdco/bills/customers/current-cycle?customerId=${customerId}`
    );
  }

  searchBillsByDates(billDate?: string, billPaidDate?: string): Observable<any> {
    let params = '';
    if (billDate) {
      params += `billDate=${billDate}`;
    }
    if (billPaidDate) {
      params += (params ? '&' : '') + `billPaidDate=${billPaidDate}`;
    }
    return this.http.get<any>(
      `${this.baseUrl}/hawdco/bills/by-dates${params ? '?' + params : ''}`
    );
  }

  updateBillDates(billNumber: number, body: any): Observable<any> {
    return this.http.put<any>(
      `${this.baseUrl}/hawdco/bills/${billNumber}/dates`,
      body
    );
  }

  updateBillsDates(bills: any[]): Observable<any> {
    return this.http.put<any>(
      `${this.baseUrl}/hawdco/bills/dates`,
      bills
    );
  }

  getCustomersWithDues(employee: Employee): Observable<ApiResponse> {
    const payload = {
      employeeId: String(employee.employeeId),
    };
    return this.http.get<ApiResponse>(
      this.baseUrl + '/hawdco/customers/due-bills',
      { params: payload }
    );
  }

  getCustomersDueBills(customer: Customer): Observable<ApiResponse> {
    const payload = {
      customerId: String(customer.customerId),
    };
    return this.http.get<ApiResponse>(
      this.baseUrl + '/hawdco/bills/customers/due-bills',
      { params: payload }
    );
  }
  getCustomerBills(employee: Employee): Observable<ApiResponse> {
    return this.http.get<ApiResponse>(
      this.baseUrl + '/hawdco/employees/empCustomerBills?employeeId='+employee.employeeId
    );
  }

  getAllCustomerBills(
    employee: { employeeId: number }
  ) {
    const payload: any = {
      employeeId: employee.employeeId
    };
  
    // Use HttpParams to build the query string
    const params = new HttpParams({ fromObject: payload });
  
    return this.http.get<any>(
      `${this.baseUrl}/hawdco/employees/all/empCustomerBills`,
      { params }
    );
  }

  getAllCustomerBillsBasedonDate(employee:Employee,startDate:any,endDate:any){
    const payload: any = {
      employeeId: employee.employeeId,
      startDate,
      endDate
    };
  
    // Use HttpParams to build the query string
    const params = new HttpParams({ fromObject: payload });
  
    return this.http.get<any>(
      `${this.baseUrl}/hawdco/employees/all/empCustomerBills/date`,
      { params }
    );
  }

  // ----- View Bill Info: separate category endpoints -----
  private getViewBillInfoByCategory(category: string, employeeId: number, startDate: string, endDate: string): Observable<any> {
    const params = new HttpParams({
      fromObject: {
        employeeId: String(employeeId),
        startDate,
        endDate
      }
    });
    return this.http.get<any>(
      `${this.baseUrl}/hawdco/employees/all/empCustomerBills/date/${category}`,
      { params }
    );
  }

  getViewBillInfoAll(employeeId: number, startDate: string, endDate: string): Observable<any> {
    return this.getViewBillInfoByCategory('all', employeeId, startDate, endDate);
  }

  getViewBillInfoBilled(employeeId: number, startDate: string, endDate: string): Observable<any> {
    return this.getViewBillInfoByCategory('billed', employeeId, startDate, endDate);
  }

  getViewBillInfoUnbilled(employeeId: number, startDate: string, endDate: string): Observable<any> {
    return this.getViewBillInfoByCategory('unbilled', employeeId, startDate, endDate);
  }

  getViewBillInfoPaid(employeeId: number, startDate: string, endDate: string): Observable<any> {
    return this.getViewBillInfoByCategory('paid', employeeId, startDate, endDate);
  }

  getViewBillInfoUnpaid(employeeId: number, startDate: string, endDate: string): Observable<any> {
    return this.getViewBillInfoByCategory('unpaid', employeeId, startDate, endDate);
  }

  getViewBillInfoDanGuud(employeeId: number, startDate: string, endDate: string): Observable<any> {
    return this.getViewBillInfoByCategory('danguud', employeeId, startDate, endDate);
  }

  // Single-call aggregations for Billing Dashboard & Graph
  getBillingDashboardData(startDate: string, endDate: string, employeeId?: string): Observable<any> {
    let params = new HttpParams()
      .set('startDate', startDate)
      .set('endDate', endDate);
    if (employeeId) {
      params = params.set('employeeId', employeeId);
    }
    return this.http.get<any>(
      `${this.baseUrl}/hawdco/employees/billing-dashboard/data`,
      { params }
    );
  }

  getBillingGraphData(cyclePairs: { startDate: string; endDate: string }[]): Observable<any> {
    const cycles = cyclePairs.map(p => `${p.startDate}_${p.endDate}`).join(',');
    const params = new HttpParams().set('cycles', cycles);
    return this.http.get<any>(
      `${this.baseUrl}/hawdco/employees/billing-graph/data`,
      { params }
    );
  }

  getCustomerTransferReport(startDate: string, endDate: string): Observable<any> {
    const params = new HttpParams()
      .set('startDate', startDate)
      .set('endDate', endDate);
    return this.http.get<any>(
      `${this.baseUrl}/hawdco/customers/transfer-report`,
      { params }
    );
  }

  getAllWagesBasedonDate(startDate:any,endDate:any,page:any,size:any){
      const payload: any = {
        startDate,
        endDate,
        page,
        size
      };
    
      // Use HttpParams to build the query string
      const params = new HttpParams({ fromObject: payload });
    
      return this.http.get<any>(
        `${this.baseUrl}/hawdco/wages/paymentdate`,
        { params }
      );
  }

  getAllBillsBasedonDate(startDate:any,endDate:any,page:any,size:any){
      const payload: any = {
        startDate,
        endDate,
        page,
        size
      };
    
      // Use HttpParams to build the query string
      const params = new HttpParams({ fromObject: payload });
    
      return this.http.get<any>(
        `${this.baseUrl}/hawdco/bills/date`,
        { params }
      );
  }

  getCurrentCycleEmployeeSummary() {
    return this.http.get<any[]>(
      `${this.baseUrl}/hawdco/bills/employees/current-cycle-summary`
    );
  }

  getBillingSummaryByDateRange(startDate: string, endDate: string, excludeDanGuud = false) {
    const params: any = {
      startDate: startDate,
      endDate: endDate,
      excludeDanGuud: excludeDanGuud ? 'true' : 'false'
    };
    return this.http.get<any[]>(
      `${this.baseUrl}/hawdco/bills/employees/billing-summary-by-date`,
      { params }
    );
  }

  getBillingSummaryDistinctTotals(excludeDanGuud = false) {
    const params: any = {
      excludeDanGuud: excludeDanGuud ? 'true' : 'false'
    };
    return this.http.get<any>(
      `${this.baseUrl}/hawdco/bills/employees/billing-summary-distinct-totals`,
      { params }
    );
  }

  generateExpense(wages : any){
      return this.http.post<any>(
      this.baseUrl + '/hawdco/wages/expensegenerate',
      wages
    );
  }

  generateGuno(wages : any){
      return this.http.post<any>(
      this.baseUrl + '/hawdco/wages/gunogenerate',
      wages
    );
  }

  // getAllCustomerBills(employee: Employee,  pageNumber: any=0,
  //   pageSize: any=15,
  //   filterOption: any=0,
  //   filterValue: any=""){
  //     const payload = {
  //       employeeId: employee.employeeId,
  //       page: pageNumber,
  //       size: pageSize,
  //       filterId: filterOption,
  //       filterValue: filterValue
  //     };

  //   return this.http.get<any>(
  //     this.baseUrl + '/hawdco/employees/all/empCustomerBills',
  //     { params : payload}
  //   );

  // }

  getComplaintsData(): Observable<Complaint[]> {
    return this.http.get<Complaint[]>(
      this.baseUrl + '/hawdco/complaint'
    );
  }

    getComplaints(page: number = 0, size: number = 10, searchValue: string = ''): Observable<any> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString());


    console.log('Complaints API params:', params.toString());

    return this.http.get<any>(
      this.baseUrl + '/hawdco/complaint/paginated',
      { params }
    );
  }

  searchComplaints(page: number = 0, size: number = 10, sortBy: string = 'complaintId', sortDir: string = 'desc', 
    searchField: string = '', searchValue: string = '', fromDate: string = '', toDate: string = ''): Observable<any> {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString())
      .set('sortBy', sortBy)
      .set('sortDir', sortDir)
      .set('searchField', searchField)
      .set('searchValue', searchValue)
      .set('fromDate', fromDate)
      .set('toDate', toDate);

    console.log('Search API params:', params.toString());
    
    return this.http.get<any>(
      this.baseUrl + '/hawdco/complaint/search',
      { params }
    );
  }

  getComplaintsDataPaginated(page: number = 0, size: number = 10, sortBy: string = 'complaintId', sortDir: string = 'desc'): Observable<any> {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString())
      .set('sortBy', sortBy)
      .set('sortDir', sortDir);

    return this.http.get<any>(
      this.baseUrl + '/hawdco/complaint/paginated',
      { params }
    );
  }

  resolveComplaint(complaint: Complaint): Observable<ApiResponse> {
  
    return this.http.put<ApiResponse>(
      this.baseUrl + '/hawdco/complaint',
      complaint
    );
    
  }




  deleteCustomer(customer: Customer): Observable<ApiResponse> {
    const payload = {
      customerId: String(customer.customerId),
    };
    return this.http.delete<ApiResponse>(
      this.baseUrl + '/hawdco/customers',
      { params: payload }
    );
  }
  addCustomer(customer: Customer): Observable<Customer> {
    return this.http.post<Customer>(
      this.baseUrl + '/hawdco/customers',
      customer
    );
  }

  bulkUploadCustomers(customers: Customer[]): Observable<any> {
    return this.http.post<any>(
      `${this.baseUrl}/hawdco/customers/bulk-upload`,
      customers
    );
  }

  addWage(wage: any): Observable<any> {
    return this.http.post<any>(
      this.baseUrl + '/hawdco/wages',
      wage
    );
  }

  getWageAuditByWageId(wageId: number) {
  return this.http.get<any[]>(`${this.baseUrl}/hawdco/wages/audit/${wageId}`);
}


  updateWage(wage: any): Observable<any> {
  return this.http.put(`${this.baseUrl}/hawdco/wages/${wage.wageId}`, wage);
}


  generateWagesForAllEmployees(payload :any) {
     return this.http.post<any>(
      this.baseUrl + '/hawdco/wages/generate-all',
      {payload}
    );
  }



   getWages(
    pageNumber: any=0,
    pageSize: any=15,
    filterOption: any=0,
    filterValue: any=""
  ): Observable<ApiResponse> {
    
    const payload = {
      page: pageNumber,
      size: pageSize,
      filterId: filterOption,
      filterValue: filterValue
    };
    return this.http.get<ApiResponse>(
      this.baseUrl + '/hawdco/wages',
      { params: payload }
    );
  }

  addCategory(categoryName: String): Observable<ApiResponse> {
    
    return this.http.post<ApiResponse>(
      this.baseUrl + '/hawdco/expense/category',
      categoryName
    );
  }

  
 

  

  addComplaint(complaintData: any): Observable<ApiResponse> {
    
    return this.http.post<any>(
      this.baseUrl + '/hawdco/complaint',
      complaintData
    );
  }


  deleteCategory(categoryId:number): Observable<ApiResponse> {
    const payload = {
      categoryId: String(categoryId),
    };
    return this.http.delete<ApiResponse>(
      this.baseUrl + '/hawdco/expense/category',
      { params: payload }
    );
  }
  addSubCategory(subCategoryName: String,categoryId:number): Observable<ApiResponse> {
    
    return this.http.post<ApiResponse>(
      this.baseUrl + '/hawdco/expense/subcategory',
      { categoryId,subCategoryName }
    );
  }
  deleteSubCategory(subCategoryId:number): Observable<ApiResponse> {
    const payload = {
      subcategoryId: String(subCategoryId),
    };
    return this.http.delete<ApiResponse>(
      this.baseUrl + '/hawdco/expense/subcategory',
      { params: payload }
    );
  }
  addSuperSubCategory(superSubCategoryName: String,subCategoryId:number): Observable<ApiResponse> {
    
    return this.http.post<ApiResponse>(
      this.baseUrl + '/hawdco/expense/super-subcategory',
      {superSubCategoryName,subCategoryId}
    );
  }
  deleteSuperSubCategory(superSubCategoryId:number): Observable<ApiResponse> {
    const payload = {
      supersubcategoryId: String(superSubCategoryId),
    };
    return this.http.delete<ApiResponse>(
      this.baseUrl + '/hawdco/expense/super-subcategory',
      { params: payload }
    );
  }

  getAllCategories():Observable<ApiResponse> {
    return this.http.get<ApiResponse>(
      this.baseUrl + '/hawdco/expense/category'
    );
  }
  getAllSubCategories(categoryId:number):Observable<ApiResponse> {
    return this.http.get<ApiResponse>(
      this.baseUrl + '/hawdco/expense/subcategory/'+categoryId
    );
  }
  getAllSuperSubCategories(subcategoryId:number):Observable<ApiResponse> {
    return this.http.get<ApiResponse>(
      this.baseUrl + '/hawdco/expense/super-subcategory/'+subcategoryId
    );
  }
  addExpense(newExpense: FormData):Observable<ApiResponse> {
    return this.http.post<ApiResponse>(
      this.baseUrl + '/hawdco/expense/add',newExpense
    );
  }
  getAllExpenseDetails():Observable<ApiResponse>{
    return this.http.get<ApiResponse>(
      this.baseUrl + '/hawdco/expense/all'
    );

  }
  approveExpense(approverLevel: number,expenseId:number,approverId:String): Observable<ApiResponse> {
    
    return this.http.post<ApiResponse>(
      this.baseUrl + '/hawdco/expense/approve',
      {expenseId,approverLevel,approverId}
    );
  }
  rejectExpense(approverLevel: number,expenseId:number,approverId:String): Observable<ApiResponse> {
    
    return this.http.post<ApiResponse>(
      this.baseUrl + '/hawdco/expense/reject',
      {expenseId,approverLevel,approverId}
    );
  }

  updateCustomer(customer: Customer): Observable<ApiResponse> {
    return this.http.put<ApiResponse>(
      this.baseUrl + '/hawdco/customers',
      customer
    );
  }

  getEmployees(): Observable<ApiResponse> {
    return this.http.get<ApiResponse>(
      this.baseUrl + '/hawdco/employees'
    );
  }

 
  getEmployeeDetailsById(employeeId: any): Observable<ApiResponse> {
    return this.http.get<ApiResponse>(
      this.baseUrl + '/hawdco/employees/details?employeeId='+employeeId
    );
  }
  updateEmployee(employee: Employee): Observable<ApiResponse> {
    return this.http.put<ApiResponse>(
      this.baseUrl + '/hawdco/employees',
      employee
    );
  }

  refreshAllListCollector(): Observable<ApiResponse> {
    return this.http.get<ApiResponse>(
      this.baseUrl + '/hawdco/employees/refresh/all-list-collector');
  }


  refreshListCollector(): Observable<ApiResponse> {
    return this.http.get<ApiResponse>(
      this.baseUrl + '/hawdco/employees/refresh/list-collector');
  }
  
  addEmployee(employee: Employee): Observable<Employee> {
    return this.http.post<Employee>(
      this.baseUrl + '/hawdco/employees',
      employee
    );
  }


  deleteEmployees(employee: Employee): Observable<ApiResponse> {
    const payload = {
      employeeId: String(employee.employeeId),
    };
    return this.http.delete<ApiResponse>(
      this.baseUrl + '/hawdco/employees',
      { params: payload }
    );
  }



  getCustomerById(id: number): Observable<ApiResponse> {
    return this.http.get<ApiResponse>(this.baseUrl + id);
  }

  getBills(customer: Customer): Observable<ApiResponse> {
    const payload = {
      customerId: String(customer.customerId),
    };
    return this.http.get<ApiResponse>(
      this.baseUrl + '/hawdco/bills/customers',
      { params: payload }
    );
  }

   getBillPayments(
    bill:Bill,
    pageNumber: any=0,
    pageSize: any=15
  ): Observable<ApiResponse> {
    const payload = {
      billNum: bill.billNumber.toString(),
      page: pageNumber,
      size: pageSize
    };
    return this.http.get<ApiResponse>(
      this.baseUrl + '/hawdco/bills/payments',
      { params: payload }
    );
  }


  updateBillDetails(bill: Bill): Observable<ApiResponse> {
    return this.http.put<ApiResponse>(
      this.baseUrl + '/hawdco/bills',
      bill
    );
  }
  saveBillDetails(bill: Bill): Observable<Bill> {
    return this.http.post<Bill>(
      this.baseUrl + '/hawdco/bills/admin',
      bill
    );
  }
  getQiyaasEstimate(customerId: number): Observable<any> {
    return this.http.get<any>(
      this.baseUrl + '/hawdco/bills/qiyaas-estimate/' + customerId
    );
  }
  getBillProof(url: string) {
    return this.http.get(this.baseUrl + url, { responseType: 'blob' });
  }

  getDasboradDetails(employeeId: number): Observable<DashboardData> {
    return this.http.get<DashboardData>(
      this.baseUrl + '/hawdco/raw/dashboard?employeeId=' + employeeId
    );
  }

  getMeterDetails(
    pageNumber: any = 0,
    pageSize: any = 15,
    search: any = '',
    employeeId: any = null
  ): Observable<ApiResponse> {
    const payload: any = {
      page: pageNumber,
      size: pageSize,
      search: search
    };
    if (employeeId != null && employeeId !== '') {
      payload.employeeId = employeeId;
    }
    return this.http.get<ApiResponse>(
      this.baseUrl + '/hawdco/meters',
      { params: payload }
    );
  }

  addMeter(meter: MeterData): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(
      this.baseUrl + '/hawdco/meters',
      meter
    );
  }

  enableMeter(meter: MeterData): Observable<ApiResponse> {
    return this.http.put<ApiResponse>(
      this.baseUrl + '/hawdco/meters',
      meter
    );
  }

  updateMeter(meter: MeterData): Observable<ApiResponse> {
    return this.http.put<ApiResponse>(
      this.baseUrl + '/hawdco/meters',
      meter
    );
  }

  bulkUpdateMeters(meters: any[]): Observable<any> {
    return this.http.post<any>(
      `${this.baseUrl}/hawdco/meters/bulk-update`,
      meters
    );
  }

  getAreaDetails(): Observable<ApiResponse> {
    return this.http.get<ApiResponse>(
      this.baseUrl + '/hawdco/areas'
    );
  }

  getRolesDetails(){
    return this.http.get<ApiResponse>(
      this.baseUrl + '/hawdco/employees/roles'
    );
  }

  addEmplyee(employee: Employee): Observable<Employee> {
    return this.http.post<Employee>(
      this.baseUrl + '/hawdco/employees',
      employee
    );
  }

  getEmplyeeDetailsById(employeeId): Observable<ApiResponse> {
    return this.http.get<ApiResponse>(
      this.baseUrl + '/hawdco/employees/details?employeeId='+employeeId
    );
  }

  getEmplyees(): Observable<ApiResponse> {
    return this.http.get<ApiResponse>(
      this.baseUrl + '/hawdco/employees'
    );
  }

  updateEmployeeRolesAndAreas(employeeData: any): Observable<any> {
    return this.http.put<any>(`${this.baseUrl}/hawdco/employees/updateRolesAndAreas`, employeeData);
  }


  updatePassword(employeeData: any): Observable<any> {
    return this.http.put<any>(`${this.baseUrl}/hawdco/employees/updatePassword`, employeeData);
  }


  addNewArea(area: AreaData): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(
      this.baseUrl + '/hawdco/areas',
      area
    );
  }

  deleteArea(area: AreaData): Observable<ApiResponse> {
    const payload = {
      areaCode: String(area.areaCode),
    };
    return this.http.delete<ApiResponse>(
      this.baseUrl + '/hawdco/areas',
      { params: payload }
    );
  }

  setUnitrate(unitrate: string): Observable<ApiResponse> {
    const payload = {
      unitRate: unitrate,
    };
    return this.http.put<ApiResponse>(
      this.baseUrl + '/hawdco/customers/unitrate?unitRate='+unitrate,
      { params: payload }
    );
  }

  getTotalCustomersCount(): Observable<ApiResponse> {
    return this.http.get<ApiResponse>(
      this.baseUrl + '/hawdco/customers/customerCount'
    );
  }

  getAddressFromLatLng(latlng: string): Observable<ApiResponse> {
    const payload = {
      latlng: latlng,
      key: environment.googleMapsApi
    };
    return this.http.get<ApiResponse>(
      'https://maps.googleapis.com/maps/api/geocode/json',
      { params: payload }
    );
  }
  deleteBillNumber(billNumber: Number): Observable<ApiResponse> {
    const payload = {
      billNumber: String(billNumber),
    };
    return this.http.delete<ApiResponse>(
      this.baseUrl + '/hawdco/bills',
      { params: payload }
    );
  }

  getLatestEmployeeLocations(): Observable<any> {
    return this.http.get<any>(
      this.baseUrl + '/hawdco/locations/latest'
    );
  }

  // Payroll APIs
  getPayrollRuns(): Observable<any> {
    return this.http.get<any>(
      this.baseUrl + '/hawdco/payroll/runs'
    );
  }

  createPayrollRun(monthYear: string, notes?: string): Observable<any> {
    const loggedUserName = window.localStorage.getItem('loggedUserName') || 'system';
    const payload: any = {
      monthYear,
      createdBy: loggedUserName,
      notes
    };
    return this.http.post<any>(
      this.baseUrl + '/hawdco/payroll/runs',
      payload
    );
  }

  getPayrollRunItems(runId: number): Observable<any> {
    return this.http.get<any>(
      `${this.baseUrl}/hawdco/payroll/runs/${runId}/items`
    );
  }

  submitPayrollRun(runId: number): Observable<any> {
    return this.http.post<any>(
      `${this.baseUrl}/hawdco/payroll/runs/${runId}/submit`,
      {}
    );
  }

  approvePayrollRun(runId: number): Observable<any> {
    const loggedUserName = window.localStorage.getItem('loggedUserName') || 'system';
    return this.http.post<any>(
      `${this.baseUrl}/hawdco/payroll/runs/${runId}/approve`,
      { approvedBy: loggedUserName }
    );
  }

  postPayrollRun(runId: number): Observable<any> {
    const loggedId = window.localStorage.getItem('loggedInUser');
    const loggedUserName = window.localStorage.getItem('loggedUserName') || 'system';
    const createdBy = loggedId ? Number(loggedId) : 0;
    return this.http.post<any>(
      `${this.baseUrl}/hawdco/payroll/runs/${runId}/post`,
      { createdBy, createdName: loggedUserName }
    );
  }

  resetPayrollRun(runId: number): Observable<any> {
    return this.http.post<any>(
      `${this.baseUrl}/hawdco/payroll/runs/${runId}/reset`,
      {}
    );
  }

  updatePayrollItem(item: any): Observable<any> {
    return this.http.put<any>(
      `${this.baseUrl}/hawdco/payroll/items/${item.itemId}`,
      item
    );
  }

  // Payroll Profiles APIs
  getPayrollProfilesForEmployee(empId: number): Observable<any[]> {
    return this.http.get<any[]>(
      `${this.baseUrl}/hawdco/payroll/profiles/employees/${empId}`
    );
  }

  addPayrollProfileForEmployee(empId: number, profile: any): Observable<any> {
    return this.http.post<any>(
      `${this.baseUrl}/hawdco/payroll/profiles/employees/${empId}`,
      profile
    );
  }

  updatePayrollProfile(profile: any): Observable<any> {
    return this.http.put<any>(
      `${this.baseUrl}/hawdco/payroll/profiles/${profile.id}`,
      profile
    );
  }

  deactivatePayrollProfile(id: number): Observable<any> {
    return this.http.patch<any>(
      `${this.baseUrl}/hawdco/payroll/profiles/${id}/deactivate`,
      {}
    );
  }

  // Payroll loan/installment plan APIs
  getPayrollLoanPlan(empId: number): Observable<any[]> {
    return this.http.get<any[]>(
      `${this.baseUrl}/hawdco/payroll/loans/employees/${empId}`
    );
  }

  createPayrollLoanPlan(empId: number, payload: any): Observable<any> {
    return this.http.post<any>(
      `${this.baseUrl}/hawdco/payroll/loans/employees/${empId}`,
      payload
    );
  }

  cancelPayrollLoanPlan(planId: number): Observable<any> {
    return this.http.patch<any>(
      `${this.baseUrl}/hawdco/payroll/loans/${planId}/cancel`,
      {}
    );
  }

  payoffPayrollLoanPlan(planId: number): Observable<any> {
    return this.http.patch<any>(
      `${this.baseUrl}/hawdco/payroll/loans/${planId}/payoff`,
      {}
    );
  }

  // Payslip APIs
  getPayslip(empId: number, monthYear: string): Observable<any> {
    const params = new HttpParams({ fromObject: { empId: String(empId), monthYear } });
    return this.http.get<any>(
      `${this.baseUrl}/hawdco/payroll/payslips`,
      { params }
    );
  }

  getPayslipPdfUrl(empId: number, monthYear: string): string {
    const query = `empId=${encodeURIComponent(String(empId))}&monthYear=${encodeURIComponent(monthYear)}`;
    return `${this.baseUrl}/hawdco/payroll/payslips/pdf?${query}`;
  }

  // Monthly payroll wage/expense summary
  getMonthlyWageReport(monthYear: string): Observable<any> {
    const params = new HttpParams({ fromObject: { monthYear } });
    return this.http.get<any>(
      `${this.baseUrl}/hawdco/wages/monthly-report`,
      { params }
    );
  }

  generateMonthlyWageExpense(monthYear: string): Observable<any> {
    const params = new HttpParams({ fromObject: { monthYear } });
    return this.http.post<any>(
      `${this.baseUrl}/hawdco/wages/monthly-expense`,
      null,
      { params }
    );
  }

  // Additional wage methods
  getWagesByMonth(monthYear: string, page: number = 0, size: number = 10): Observable<any> {
    const params = new HttpParams({ fromObject: { monthYear, page: String(page), size: String(size) } });
    return this.http.get<any>(
      `${this.baseUrl}/hawdco/wages/by-month`,
      { params }
    );
  }

  finalizeWagesForMonth(monthYear: string): Observable<void> {
    const params = new HttpParams({ fromObject: { monthYear } });
    return this.http.post<void>(
      `${this.baseUrl}/hawdco/wages/finalize-month`,
      null,
      { params }
    );
  }

  // Attendance APIs (ZKTeco-based)
  getAttendanceLogs(empId: number, from: string, to: string): Observable<any[]> {
    const params = new HttpParams({
      fromObject: {
        empId: String(empId),
        from,
        to
      }
    });
    return this.http.get<any[]>(
      `${this.baseUrl}/hawdco/attendance/logs`,
      { params }
    );
  }

  getAttendanceSummary(empId: number, from: string, to: string): Observable<any[]> {
    const params = new HttpParams({
      fromObject: {
        empId: String(empId),
        from,
        to
      }
    });
    return this.http.get<any[]>(
      `${this.baseUrl}/hawdco/attendance/summary`,
      { params }
    );
  }

  // Bank amount related methods
  getAllBankAmounts(): Observable<BankAmountResponse> {
    return this.http.get<BankAmountResponse>(
      `${this.baseUrl}/hawdco/bank/bank-amounts`
    );
  }

  getBankAmountsByEmployeeId(employeeId: number): Observable<BankAmountResponse> {
    return this.http.get<BankAmountResponse>(
      `${this.baseUrl}/hawdco/bank/bank-amounts?employeeId=${employeeId}`
    );
  }

  saveBankAmount(bankRequest: BankAmountRequest): Observable<any> {
    return this.http.post<any>(
      `${this.baseUrl}/hawdco/bank/bank-amounts`,
      bankRequest
    );
  }

  updateBankAmount(id: number, bankRequest: BankAmountRequest): Observable<any> {
    return this.http.put<any>(
      `${this.baseUrl}/hawdco/bank/bank-amounts/${id}`,
      bankRequest
    );
  }

  deleteBankAmount(id: number): Observable<ApiResponse> {
    return this.http.delete<ApiResponse>(
      `${this.baseUrl}/hawdco/bank/bank-amounts/${id}`
    );
  }

  getBankAmountsByDateRange(startDate: string, endDate: string): Observable<BankAmountResponse> {
    const params = {
      startDate: startDate,
      endDate: endDate
    };
    return this.http.get<BankAmountResponse>(
      `${this.baseUrl}/hawdco/bank/bank-amounts/date`,
      { params }
    );
  }

  uploadAccountData(formData: FormData): Observable<any> {
    return this.http.post<any>(
      `${this.baseUrl}/hawdco/account-upload/upload`,
      formData
    );
  }

  updateBillPartialAmounts(request: BillUpdateRequest): Observable<any> {
    return this.http.post<any>(
      `${this.baseUrl}/hawdco/account-upload/update-bills`,
      request
    );
  }

  matchPhonesFromExistingData(empId: number, cycleStart: string, cycleEnd: string): Observable<PhoneMatchResult[]> {
    const params = new HttpParams()
      .set('empId', empId.toString())
      .set('billingCycleStart', cycleStart)
      .set('billingCycleEnd', cycleEnd);
    
    return this.http.get<PhoneMatchResult[]>(
      `${this.baseUrl}/hawdco/account-upload/match-from-existing`,
      { params }
    );
  }

  getAccountUploadPaymentHistory(custId: number, phone: string, cycleEnd: string): Observable<any[]> {
    const params = new HttpParams()
      .set('custId', custId.toString())
      .set('phone', phone || '')
      .set('cycleEnd', cycleEnd);

    return this.http.get<any[]>(
      `${this.baseUrl}/hawdco/account-upload/payment-history`,
      { params }
    );
  }

  uploadMerchantData(formData: FormData): Observable<any> {
    return this.http.post<any>(
      `${this.baseUrl}/hawdco/merchant-upload/upload`,
      formData
    );
  }

  getMerchantUploadsPaginated(empId: number, startDate: string, endDate: string, page: number, size: number): Observable<any> {
    const params = new HttpParams()
      .set('empId', empId.toString())
      .set('startDate', startDate)
      .set('endDate', endDate)
      .set('page', page.toString())
      .set('size', size.toString());
    return this.http.get<any>(
      `${this.baseUrl}/hawdco/merchant-upload/paginated`,
      { params }
    );
  }

  getAccountUploadsPaginated(empId: number, startDate: string, endDate: string, page: number, size: number): Observable<any> {
    const params = new HttpParams()
      .set('empId', empId.toString())
      .set('startDate', startDate)
      .set('endDate', endDate)
      .set('page', page.toString())
      .set('size', size.toString());
    return this.http.get<any>(
      `${this.baseUrl}/hawdco/account-upload/paginated`,
      { params }
    );
  }

  getMerchantUploadSums(empId: number, startDate: string, endDate: string): Observable<any> {
    const params = new HttpParams()
      .set('empId', empId.toString())
      .set('startDate', startDate)
      .set('endDate', endDate);
    return this.http.get<any>(
      `${this.baseUrl}/hawdco/merchant-upload/sums`,
      { params }
    );
  }

  getAccountUploadSums(empId: number, startDate: string, endDate: string): Observable<any> {
    const params = new HttpParams()
      .set('empId', empId.toString())
      .set('startDate', startDate)
      .set('endDate', endDate);
    return this.http.get<any>(
      `${this.baseUrl}/hawdco/account-upload/sums`,
      { params }
    );
  }

  getMerchantUploadSumsAll(startDate: string, endDate: string): Observable<Map<number, Map<string, number>>> {
    const params = new HttpParams()
      .set('startDate', startDate)
      .set('endDate', endDate);
    return this.http.get<Map<number, Map<string, number>>>(
      `${this.baseUrl}/hawdco/merchant-upload/sums-all`,
      { params }
    );
  }

  getAccountUploadSumsAll(startDate: string, endDate: string): Observable<Map<number, Map<string, number>>> {
    const params = new HttpParams()
      .set('startDate', startDate)
      .set('endDate', endDate);
    return this.http.get<Map<number, Map<string, number>>>(
      `${this.baseUrl}/hawdco/account-upload/sums-all`,
      { params }
    );
  }

}
